import { useMemo } from "react";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import { toast } from "sonner";
import LoadTemplates from "../../components/modals/LoadTemplates";
import { useScheduleContext } from "../context/useScheduleContext";
import { useFilters } from "../../../../../utils/hooks/useFilters";
import useApi from "../../../../../utils/api";
import { useVisible } from "../../../../../utils/hooks/useVisible";
import { formatTimeSheetParams } from "../../../../TimeSheet/api/helpers";
import { getErrorMessage } from "../../../../../utils/helpers/errors";

export const useLoadTemplatesModal = () => {
    const { t } = useTranslation();
    const [filters] = useFilters();
    const { authPost } = useApi();
    const { open, close, visible, selected } = useVisible();
    const { dispatch, resetCount, refetchEventStatistics } = useScheduleContext();

    const loadTemplatesQuery = useMutation({
        mutationFn: (templateId: string) => {
            const from = selected?.type === "day" ? selected?.date : filters?.from;
            const to = selected?.type === "day" ? selected?.date : filters?.to;

            const params = {
                ...formatTimeSheetParams({
                    filters: filters?.filters || [],
                    from: filters?.mode === "month" ? moment(from).startOf("week").format("YYYY-MM-DD") : from,
                    to: filters?.mode === "month" ? moment(to).endOf("week").format("YYYY-MM-DD") : to
                }),
                pageIndex: Number(filters?.page || 1) - 1,
                pageSize: Number(filters?.pageSize || 50),
                sort: filters?.sort
            };
            return authPost(`/shift-templates/v2/${templateId}/generate`, { params });
        },
        onMutate: () => toast.loading(t("loading-day-template")),
        onSuccess: (_data, _vars, toastId) => {
            toast.dismiss(toastId);
            toast.success(t("template-loaded-successfully"));
            dispatch({ type: "refetch-events" });
            resetCount?.();
            refetchEventStatistics();
            close();
        },
        onError: (err: any, _vars, toastId) => {
            toast.dismiss(toastId);
            getErrorMessage(err, t);
        }
    })

    const modal = useMemo(() => {
        return (
            <LoadTemplates
                visible={visible}
                selected={selected}
                close={close}
                onFinish={loadTemplatesQuery.mutate}
                loading={loadTemplatesQuery.isPending}
            />
        );
    }, [visible, selected, loadTemplatesQuery, close]);

    return { open, modal };
}
